import { Subject } from 'rxjs';
import { Component, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { StaffLoginService } from 'src/app/services/staff-login/staff-login.service';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { Location } from '@angular/common';
import { GoogleAuthService } from 'src/app/services/google-auth/google-auth.service';
import { MicrosoftAuthService } from 'src/app/services/microsoft-auth/microsoft-auth.service';
import { collapseOnLeaveAnimation, expandOnEnterAnimation } from 'angular-animations';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
  selector: 'app-login-section',
  templateUrl: './login-section.component.html',
  styleUrls: ['./login-section.component.scss'],
  animations: [expandOnEnterAnimation(), collapseOnLeaveAnimation()]
})
export class LoginSectionComponent implements OnInit, OnDestroy {

  @Output("changeForm") changeForm: EventEmitter<any> = new EventEmitter();

  loginForm: FormGroup;
  updatePasswordForm: FormGroup;

  auth_modules: any[] = [];
  client_details: any[] = [];

  verify_otp_error_msg: string = '';
  update_pass_error_msg: string = '';
  verify_otp_success_msg: string = '';

  is_logging: boolean = false;
  is_passReset: boolean = false;
  is_verifyingOtp: boolean = false;
  is_passUpdating: boolean = false;
  is_force_update_pass: boolean = false;
  is_access_authorised: boolean = false;
  is_pass_type_password: boolean = false;
  is_old_pass_type_password: boolean = false;
  is_new_pass_type_password: boolean = false;
  show_password_field: boolean = false;
  submitted: boolean = false;
  policyType: 'standard' | 'strong' = 'standard';
  google_signin_loading: boolean = false;
  is_from_verify_account: boolean = false;
  is_initial_logging: boolean = true;
  is_initial_g_logging: boolean = false;
  microsoft_signin_loading: boolean = false;
  sso_signin_loading: boolean = false;
  az_login_response: any;
  is_initial_az_logging: boolean = false;
  is_saml_logging: boolean = false;
  saml_token: string = '';

  error_msg: string = '';

  login_response: any;
  client_response: any;
  verify_otp_response: any;
  pass_update_response: any;

  // MFA login flow state. Triggered when /auth/login returns { data: { requiresMfa: true, ... } }.
  is_mfaPending: boolean = false;
  is_mfaVerifying: boolean = false;
  is_mfaResending: boolean = false;
  mfa_error_msg: string = '';
  mfa_userId: string = '';
  mfa_uid: string = '';
  mfa_subtitle: string = '';

  // Max-attempts UX: inline message + 5s countdown + manual "Go to login now" link.
  // String stays FE-owned per spec (BE has no per-attempt state to know when to emit it).
  private readonly MFA_MAX_ATTEMPTS_MSG = 'Maximum OTP attempts exceeded. Please login again.';
  mfa_max_attempts_reached: boolean = false;
  mfa_redirect_countdown: number = 5;
  private mfaRedirectTimerId: any = null;

  // Tick counters drive @Input changes on <app-otp-verification> for invalid-OTP + resend-start.
  mfa_invalidOtpTick: number = 0;
  mfa_resendStartTick: number = 0;
  // Bumped on each MFA entry to fully reset the cached <app-otp-verification> for a new cycle.
  mfa_resetTick: number = 0;

  g_login_response: any;

  selectedIndex: any = 0;

  countdown: number = -1;
  // Tick bumped on successful (re)send to start cooldown inside <app-otp-verification> for the force-update tab.
  force_update_resendStartTick: number = 0;

  sessionData: any;

  // Property to track popup window
  private popupWindow: Window | null = null;

  private _onDestroy = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private staffLoginService: StaffLoginService,
    public router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private authService: AuthService,
    private googleAuthService: GoogleAuthService,
    private microsoftAuthService: MicrosoftAuthService,
  ) {
    this.loginForm = this.formBuilder.group({
      mobile_email: ['', [Validators.required]],
      password: ['']
    });

    this.updatePasswordForm = this.formBuilder.group({
      otp_ctrl: ['', [Validators.required]],
      new_password: ['', [this.gatedPasswordValidator()]],
      confirm_password: ['', [this.gatedConfirmPasswordValidator()]]
    });

    this.sessionData = this.route.snapshot.data.sessionData;
    if (this.sessionData && this.sessionData?.data) {
      this.login_response = this.sessionData;
      this.loginSessionRes();
    };

    const loginResponseData = this.authService.getLoginResponseData();
    if (loginResponseData) {
      this.login_response = loginResponseData;
      this.is_from_verify_account = true;
      this.show_password_field = true;
      this.loginForm.get('mobile_email').setValue(this.login_response?.data?.mobile ? this.login_response?.data?.mobile : this.login_response?.data?.office_email);
      this.loginForm.get('password').setValue(this.login_response?.password);
      this.getLoginDetails('signup_user');
      this.loginForm.disable();
    }
   }

  ngOnInit(): void {

    this.loginForm.valueChanges.subscribe(value => {
      this.error_msg = '';
      this.verify_otp_error_msg = '';
    });

    this.updatePasswordForm.valueChanges.subscribe(value => {
      this.update_pass_error_msg = '';
    });

    // Clear field-level error on the next edit after a failed submit.
    this.updatePasswordForm.get('new_password').valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        if (this.submitted) {
          this.submitted = false;
          this.updatePasswordForm.get('new_password').updateValueAndValidity({ emitEvent: false });
          this.updatePasswordForm.get('confirm_password').updateValueAndValidity({ emitEvent: false });
        }
      });

    this.updatePasswordForm.get('confirm_password').valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        if (this.submitted) {
          this.submitted = false;
          this.updatePasswordForm.get('new_password').updateValueAndValidity({ emitEvent: false });
          this.updatePasswordForm.get('confirm_password').updateValueAndValidity({ emitEvent: false });
        }
      });

    this.googleAuthService.googleSignInLoading.subscribe(value => {
      this.google_signin_loading = value;
    });

    this.microsoftAuthService.microsoftSignInLoading.subscribe(value => {
      this.microsoft_signin_loading = value;
    });
  }

  checkUppercase(control) {
    return control.value && control.value.match(/[A-Z]/) ? null : { uppercase: true }
  }

  checkNumber(control) {
    return control.value && control.value.match(/[0-9]/) ? null : { number: true }
  }

  newPasswordHasUppercase(): boolean {
    return /[A-Z]/.test(this.updatePasswordForm?.get('new_password')?.value ?? '');
  }

  newPasswordHasNumber(): boolean {
    return /[0-9]/.test(this.updatePasswordForm?.get('new_password')?.value ?? '');
  }

  newPasswordHasLowercase(): boolean {
    return /[a-z]/.test(this.updatePasswordForm?.get('new_password')?.value ?? '');
  }

  newPasswordHasSpecialChar(): boolean {
    return /[^A-Za-z0-9]/.test(this.updatePasswordForm?.get('new_password')?.value ?? '');
  }

  private gatedPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.submitted) return null;
      const value: string = control.value ?? '';
      if (!value) return { required: true };
      const errors: ValidationErrors = {};
      if (value.length < 8) errors['minlength'] = { requiredLength: 8, actualLength: value.length };
      if (value.length > 20) errors['maxlength'] = { requiredLength: 20, actualLength: value.length };
      if (!value.match(/[A-Z]/)) errors['uppercase'] = true;
      if (!value.match(/[0-9]/)) errors['number'] = true;
      if (this.policyType === 'strong') {
        if (!value.match(/[a-z]/)) errors['lowercase'] = true;
        if (!value.match(/[^A-Za-z0-9]/)) errors['specialChar'] = true;
      }
      return Object.keys(errors).length === 0 ? null : errors;
    };
  }

  private gatedConfirmPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.submitted) return null;
      const value: string = control.value ?? '';
      if (!value) return { required: true };
      const newPassword = this.updatePasswordForm?.get('new_password')?.value ?? '';
      if (value !== newPassword) return { mismatch: true };
      return null;
    };
  }

  getLoginDetails(auth_type) {
    this.error_msg = '';

    // Close popup window if it's open
    if (this.popupWindow && !this.popupWindow.closed) {
      this.popupWindow.close();
      this.popupWindow = null;
    }

    let credentials: any = {
      via: 'web_dashb'
    };

    this.is_logging = true;

    if (this.show_password_field) {
      credentials.username = this.loginForm.get('mobile_email').value.trim();
      credentials.password = this.loginForm.get('password').value;
    } else {
      credentials.username = this.loginForm.get('mobile_email').value.trim();
    };

    if (auth_type == 'g_auth') {
      credentials.g_email = this.g_login_response.user_email;
      credentials.g_id_token = this.g_login_response.user_id_token;
      credentials.g_client_id = this.g_login_response.google_client_id;
      credentials.username = this.g_login_response.user_email;

      if (this.is_initial_g_logging) {
        credentials.call_from = 'g_login';
      } else {
        credentials.call_from = 'g_skip_pass';
      };
    } else if (auth_type == 'az_auth') {
      credentials.az_email = this.az_login_response.user_email;
      credentials.az_id_token = this.az_login_response.user_id_token;
      credentials.az_client_id = this.az_login_response.microsoft_client_id;
      credentials.username = this.az_login_response.user_email;

      if (this.is_initial_az_logging) {
        credentials.call_from = 'az_login';
      } else {
        credentials.call_from = 'az_skip_pass';
      }
    } else if (auth_type == 'saml_auth') {
      credentials.saml_token = this.saml_token;
      credentials.call_from = 'saml_skip_pass';
    };

    this.staffLoginService.login(credentials).pipe(takeUntil(this._onDestroy)).subscribe((res: any) => {
      if (res?.data?.[0]) {
        res.data = res?.data[0];
      };
      this.login_response = res;

      if (this.login_response?.code == '1') {
        if (this.login_response?.data?.requiresMfa === true) {
          this.enterMfaStep(this.login_response);
          return;
        }
        if (auth_type == 'g_auth' && this.is_initial_g_logging) {
          this.is_initial_g_logging = false;
          this.getLoginDetails('g_auth');
        } else if (auth_type == 'az_auth' && this.is_initial_az_logging) {
          this.is_initial_az_logging = false;
          this.getLoginDetails('az_auth');
        } else if (this.login_response?.data?.existing_user == '1') {
          this.show_password_field = true;
          this.is_initial_logging = false;
          this.is_logging = false;
          this.loginForm.get('password').setValidators([Validators.maxLength(20)]);
          this.loginForm.get('password').updateValueAndValidity();

          if (this.login_response?.data?.saml_sso_url) {
            this.openSSOUrl();
          }

          setTimeout(() => {
            this.loginForm.get('password').markAsUntouched();

            const passwordInput = document.querySelector('input[formControlName="password"]') as HTMLInputElement;
            if (passwordInput) {
              passwordInput.focus();
            };
          }, 100);

        } else if (this.login_response?.data?.existing_user == '0' && this.login_response?.data?.invited_signup == '1') {
          this.is_from_verify_account = true;
          this.is_logging = true;
          this.selectedIndex = 3;
        } else {
          this.loginSessionRes();
        };
      } else {
        this.error_msg = this.login_response?.message;
        this.is_logging = false;
        this.loginForm.get('password').setValidators([]);
        this.updateGoogleSignInRes();
      };
    }, error => {
      this.loginForm.get('password').setValidators([]);

      console.log('error', error);
      this.error_msg = `Error in recieving data from server, please try again later!`;
      this.is_logging = false;
      this.updateGoogleSignInRes();
    });
  }

  loginSessionRes() {
    if(this.login_response?.code == '0' || this.login_response?.code == '4') {
      this.error_msg = this.login_response?.message;
      if(this.login_response?.data?.is_verified == '0') {
        const redirect_url: any =  window.location.hostname.includes('truein.app') ? 'https://truein.app/gotruein.dev/dashboard/account/' : 'https://dashboard.truein.com/account/';
        window.location = redirect_url;
      };
      this.updateGoogleSignInRes();
      this.is_logging = false;
    } else if(this.login_response?.code == '1' || this.login_response?.code == '2') {
      this.afterSuccessLoginRes();
    } else if(this.login_response?.code == '6') {
      // window.location = "setup-client";
      this.updateGoogleSignInRes();
      this.is_logging = false;
    } else {
      this.error_msg = `Error! Please Contact <a href="mailto:support@truein.com">support@truein.com</a>`;
      this.is_logging = false;
      this.updateGoogleSignInRes();
    };
  }

  afterSuccessLoginRes() {
    this.client_details = this.login_response?.data?.client_details;
    this.auth_modules = this.login_response?.data?.authorised_modules ? this.login_response?.data?.authorised_modules.replace(' ','').split(',') : [];
    if(this.login_response?.data?.force_update_password == '1') {
      this.policyType = this.login_response?.data?.fnPasswordType || 'standard';
      if (!this.loginForm.get('mobile_email').value) {
        this.loginForm.get('mobile_email').setValue(this.login_response?.data?.mobile ? this.login_response?.data?.mobile : this.login_response?.data?.office_email);
      };
      this.forgotPassword();
      this.updatePasswordForm.markAsUntouched();
    }
    // else if((this.login_response?.data?.sms_verified != '1' && this.login_response?.data?.mobile != '') || (this.login_response?.data?.email_verified != '1' && this.login_response?.data?.office_email != '')) {
    //   this.selectedIndex = 3;
    // }
    else if(this.client_details.length > 1) {
      this.getSelectOrgTab();
    } else {
      if(this.login_response?.data?.authorised_modules.match(this.login_response?.data?.last_module)) {
        window.open(`${environment.base_url}/` + this.login_response?.data?.last_module, '_self');
      } else {
        window.open(`${environment.base_url}/` + this.auth_modules[0], '_self');
      };
      this.is_access_authorised = true;
    };
    this.error_msg = '';
  }

  getSelectOrgTab() {
    this.delete_cookie('PHPSESSID');
    this.selectedIndex = 4;
  }

  nextTab() {
    // if((this.login_response?.data?.sms_verified != '1' && this.login_response?.data?.mobile != '') || (this.login_response?.data?.email_verified != '1' && this.login_response?.data?.office_email != '')) {
    //   this.selectedIndex = 3;
    // } else
    if(this.client_details.length > 1) {
      this.delete_cookie('PHPSESSID');
      this.selectedIndex = 4;
    } else {
      if(this.login_response?.data?.authorised_modules.match(this.login_response?.data?.last_module)) {
          window.open(`${environment.base_url}/` + this.login_response?.data?.last_module, '_self');
      } else {
        window.open(`${environment.base_url}/` + this.auth_modules[0], '_self');
        };
      this.is_access_authorised = true;
    };
  }

  getFieldErrorMsg(pass_field: string) {
    const errs = this.updatePasswordForm.controls[pass_field].errors;
    const parts: string[] = [];
    if (errs?.minlength) parts.push('8 characters');
    if (errs?.uppercase) parts.push('one uppercase letter');
    if (errs?.number) parts.push('one number');
    if (errs?.lowercase) parts.push('one lowercase letter');
    if (errs?.specialChar) parts.push('one special character');
    if (parts.length === 0) return '';
    if (parts.length === 1) return 'Password must contain at least ' + parts[0];
    return 'Password must contain at least ' + parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1];
  }

  validateOTP(e): boolean {
    const charCode = e.which ? e.which : e.keyCode;
    if ((charCode < 48 || charCode > 57) || e.target.value.length > 5) {
      return false
    }
    return true
  }

  sendOtp() {
    this.is_passReset = true;
    this.verify_otp_error_msg = '';

    let credentials: any = {
      username: this.loginForm.get('mobile_email').value.trim()
    };

    this.authService.sendOtp(credentials).pipe(takeUntil(this._onDestroy)).subscribe(res => {
      this.verify_otp_response = res;
    }, error => {
      console.log('error', error);
      this.verify_otp_error_msg = `Error! Please try again or contact <a href="mailto:support@truein.com">support@truein.com</a> for help.`;
      this.selectedIndex = 0;
      this.is_passReset = false;
      this.is_logging = false;
    }, () => {
      if(this.verify_otp_response.code == '1') {
        this.verify_otp_success_msg = this.verify_otp_response?.data?.message;
        this.startCountdown();
        this.force_update_resendStartTick++;
        this.is_passReset = true;
        this.selectedIndex = 1;
        this.loginForm.reset();
      } else if(this.verify_otp_response.code == '0') {
        this.verify_otp_error_msg = this.verify_otp_response?.message;
        this.is_passReset = false;
        this.is_logging = false;
      } else {
        this.verify_otp_error_msg = `Error! Please try again or contact <a href="mailto:support@truein.com">support@truein.com</a> for help.`;
        this.is_passReset = false;
        this.is_logging = false;
      }
    })
  }

  forgotPassword() {
    this.is_passReset = true;
    this.verify_otp_error_msg = '';
    const credentials: any = {
      username: this.loginForm.get('mobile_email').value ? this.loginForm.get('mobile_email')?.value?.trim() : ''
    };

    this.staffLoginService.forgotPassword(credentials).pipe(takeUntil(this._onDestroy)).subscribe(res => {
      this.verify_otp_response = res;
    }, error => {
      console.log('error', error);
      this.verify_otp_error_msg = `Error! Please try again or contact <a href="mailto:support@truein.com">support@truein.com<a> for help.`;
      this.selectedIndex = 0;
      this.is_passReset = false;
      this.is_logging = false;
    }, () => {
      if(this.verify_otp_response.code == '1') {
        this.policyType = this.verify_otp_response?.data?.fnPasswordType || this.policyType || 'standard';
        this.startCountdown();
        this.force_update_resendStartTick++;
        this.is_passReset = true;
        this.verify_otp_success_msg = this.verify_otp_response?.message;
        this.selectedIndex = 1;
      } else if(this.verify_otp_response.code == '0') {
        this.verify_otp_error_msg = this.verify_otp_response?.message;
        this.is_passReset = false;
        this.is_logging = false;
      } else {
        this.verify_otp_error_msg = `Error! Please try again or contact <a href="mailto:support@truein.com">support@truein.com<a> for help.`;
        this.is_passReset = false;
        this.is_logging = false;
      }
    })
  }

  verifyOtp() {
    const credentials: any = {
      uid: this.verify_otp_response?.data?.uid,
      token: this.verify_otp_response?.data?.token,
      otp: this.updatePasswordForm.get('otp_ctrl').value
    };

    this.staffLoginService.verifyOtp(credentials).pipe(takeUntil(this._onDestroy)).subscribe((res: any) => {
      this.verify_otp_response = res;

      if (res.code == '1') {
        this.is_passUpdating = true;
        this.changePassword();
      } else {
        this.verify_otp_error_msg = this.verify_otp_response?.message;
        this.is_passUpdating = false;
      };
    },
    err => {
      this.verify_otp_error_msg = `Error! Please try again or contact <a href="mailto:support@truein.com">support@truein.com<a> for help.`;
      this.is_passUpdating = false;
      console.log(err);
    });
  }

  changePassword() {
    this.submitted = true;
    this.updatePasswordForm.get('new_password').updateValueAndValidity({ emitEvent: false });
    this.updatePasswordForm.get('confirm_password').updateValueAndValidity({ emitEvent: false });

    if(this.updatePasswordForm.invalid) {
      this.updatePasswordForm.markAllAsTouched();
    } else if((this.updatePasswordForm.get('new_password').value !== this.updatePasswordForm.get('confirm_password').value) && this.selectedIndex == 2) {
      this.update_pass_error_msg = 'Passwords do not match';
    } else {
      this.is_passUpdating = true;

      const credentials: any = {
        password: this.updatePasswordForm.get('new_password').value,
        uid: this.verify_otp_response?.data?.uid,
        token: this.verify_otp_response?.data?.token
      };

      this.staffLoginService.changePassword(credentials).pipe(takeUntil(this._onDestroy)).subscribe((res) => {
        this.pass_update_response = res;
      }, error => {
        console.log('error', error)
        this.is_passUpdating = false;
      }, () => {
        if(this.pass_update_response.code == '1') {
          this.update_pass_error_msg = '';
          this.submitted = false;
          // this.loginPage.emit();
          this.selectedIndex = 2;
          this.updatePasswordForm.markAsUntouched();
        } else if(this.pass_update_response.code == '0') {
          this.updatePasswordForm.controls['otp_ctrl'].setErrors({ customError: this.pass_update_response.message });
        } else {
          this.update_pass_error_msg = `Error! Please try again or contact <a href="mailto:support@truein.com">support@truein.com</a> for help.`;
        };
        this.is_passUpdating = false;
      })
    }
  }

  startCountdown() {
    this.countdown = 60;
    let timer = setInterval(() => {
      this.countdown--;
      if(this.countdown == -1) {
        clearInterval(timer);
        this.is_passReset = false;
      }
    }, 1000);
  }

  validateLoginFormFields() {
    if (this.show_password_field) {
      this.loginForm.get('password').setValidators([Validators.required, Validators.maxLength(20)]);
      this.loginForm.get('password').updateValueAndValidity();
    };

    if (this.loginForm.invalid) {
      this.loginForm.markAsTouched({ onlySelf: true });
    };
  }

  updateOrgClient(client_id) {
    let credentials: any = {
      user_id: this.login_response?.data?.user_id,
      default_client_id: client_id,
      via: 'web_dashb'
    };

      this.staffLoginService.changeOrganization(credentials).pipe(takeUntil(this._onDestroy)).subscribe(res => {
        this.client_response = res;
      }, error => {
        console.log('error', error);
      }, () => {
        if (this.auth_modules.includes(this.login_response?.data?.last_module)) {
          window.open(`${environment.base_url}/` + this.login_response?.data?.last_module, '_self');
        } else {
          window.open(`${environment.base_url}/` + this.auth_modules[0], '_self');
				};
      });
  }

  delete_cookie(cookie_name) {
    document.cookie = cookie_name +'=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }

  updatePassword() {
    this.submitted = true;
    this.updatePasswordForm.get('new_password').updateValueAndValidity({ emitEvent: false });
    this.updatePasswordForm.get('confirm_password').updateValueAndValidity({ emitEvent: false });

    if(this.updatePasswordForm.invalid) {
      this.updatePasswordForm.markAllAsTouched();
    } else if((this.updatePasswordForm.get('new_password').value !== this.updatePasswordForm.get('confirm_password').value) && this.selectedIndex == 1) {
      this.update_pass_error_msg = 'Passwords do not match';
    } else {
      this.is_passUpdating = true;
      const data = {
        otp: this.updatePasswordForm.get('otp_ctrl').value,
        password: this.updatePasswordForm.get('new_password').value,
        token: this.verify_otp_response?.data?.token,
        action_type: 'update_password',
        via: 'web_login',
      };
      let credentials =
      "otp=" + data.otp +
      "&password=" + data.password +
      "&token=" + data.token +
      "&action_type=" + data.action_type +
      "&via=" + data.via

      this.staffLoginService.updatePassword(credentials).pipe(takeUntil(this._onDestroy)).subscribe((res) => {
        this.pass_update_response = res;
      }, error => {
        console.log('error', error)
        this.is_passUpdating = false;
      }, () => {
        if(this.pass_update_response.code == '1') {
          this.update_pass_error_msg = '';
          this.submitted = false;
          // this.loginPage.emit();
          this.selectedIndex = 2;
          this.updatePasswordForm.markAsUntouched();
          this.updatePasswordForm.reset();
        } else if(this.pass_update_response.code == '0') {
          this.updatePasswordForm.controls['otp_ctrl'].setErrors({ customError: this.pass_update_response.message });
        } else {
          this.update_pass_error_msg = `Error! Please try again or contact <a href="mailto:support@truein.com">support@truein.com</a> for help.`;
        };
        this.is_passUpdating = false;
      });
    }
  }

  signInWithGoogle() {
    this.googleAuthService.googleSignIn();

    this.googleAuthService.googleSignInData.pipe(takeUntil(this._onDestroy)).subscribe(response => {
      if (response) {
        if (response.error) {
          this.updateGoogleSignInRes();
        } else {
          this.g_login_response = response;
          this.is_initial_g_logging = true;
          this.getLoginDetails('g_auth');
        };
      };
    });
  }

  updateGoogleSignInRes() {
    this.googleAuthService.updateGoogleSignInRes('');
    this.google_signin_loading = false;
  }

  signInWithMicrosoft() {
    this.microsoftAuthService.microsoftSignIn();

    this.microsoftAuthService.microsoftSignInData.pipe(takeUntil(this._onDestroy)).subscribe(response => {
      if (response) {
        if (response.error || response instanceof Error) {
          this.updateMicrosoftSignInRes();
        } else {
          this.az_login_response = response;
          this.is_initial_az_logging = true;
          this.getLoginDetails('az_auth');
        }
      }
    });
  }

  updateMicrosoftSignInRes() {
    this.microsoftAuthService.updateMicrosoftSignInRes('');
    this.microsoft_signin_loading = false;
  }

  onLogOut(){
    window.open(`${environment.base_url}/logout.php`, '_self');
  }

  getResetPassTab() {
    this.changeForm.emit();
    this.location.replaceState('/reset-password');
  }

  openDashboardWindow() {
    window.open(`${environment.base_url}/staff`, '_self');
  }

  openSSOUrl() {
    this.sso_signin_loading = true;
    const width = 600;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = ((window.screen.height - height) / 2) - 50;

    // const relayState = encodeURIComponent(this.login_response?.data?.parent_client_id + '|web_dashb');

    const popup = window.open(
      this.login_response?.data?.saml_sso_url,
      'SSO Login',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
    );

    // Store popup reference
    this.popupWindow = popup;

    // Check if popup was blocked
    if (!popup) {
      this.sso_signin_loading = false;
      return;
    }

    // Check if popup is closed
    const checkPopupClosed = setInterval(() => {
      if (this.popupWindow?.closed) {
        clearInterval(checkPopupClosed);
        this.sso_signin_loading = false;
        this.popupWindow = null;
      }
    }, 500);

    // Listen for the popup window's location changes
    const checkPopupLocation = setInterval(() => {
      try {
        if (this.popupWindow?.closed) {
          clearInterval(checkPopupLocation);
          return;
        }

        // Only try to access location if we can
        if (this.popupWindow?.location.href === 'about:blank') {
          return; // Skip if we can't access the URL due to cross-origin
        }

        const popupUrl = this.popupWindow?.location.href;

        // Check if the URL contains the token
        if (popupUrl.includes('token=')) {
          const url = new URL(popupUrl);
          const token = url.searchParams.get('token');

          if (token) {
            // Store the token
            this.saml_token = token;

            // Close the popup
            this.popupWindow?.close();
            this.popupWindow = null;

            // Reset loading state
            this.getLoginDetails('saml_auth');

            // Clear intervals
            clearInterval(checkPopupLocation);
            clearInterval(checkPopupClosed);
          }
        }
      } catch (error) {
        // Silently handle cross-origin errors
        // The popup is likely still on the SSO provider's domain
      }
    }, 500);
  }

  // MFA — build the OTP subtitle from the backend's discrete masked channels
  // (maskedEmail/maskedMobile, "" when absent) instead of the generic server
  // sentence, so the user sees the actual masked email/mobile. Falls back to
  // res.message if neither field is present (older backend).
  private composeMfaSubtitle(res: any): string {
    const maskedEmail = (res?.data?.maskedEmail ?? '').toString();
    const maskedMobile = (res?.data?.maskedMobile ?? '').toString();
    const prefix = 'Verification code has been sent to';
    if (maskedEmail && maskedMobile) {
      return `${prefix} ${maskedEmail} and ${maskedMobile}`;
    } else if (maskedEmail) {
      return `${prefix} ${maskedEmail}`;
    } else if (maskedMobile) {
      return `${prefix} ${maskedMobile}`;
    }
    return res?.message ?? '';
  }

  // MFA — enter the OTP step. selectedIndex=5 hosts the MFA tab in the template.
  enterMfaStep(res: any): void {
    this.clearMfaRedirectTimer();
    this.mfa_max_attempts_reached = false;
    this.mfa_redirect_countdown = 5;
    this.mfa_userId = res?.data?.userId ?? '';
    this.mfa_uid = res?.data?.uid ?? '';
    this.mfa_subtitle = this.composeMfaSubtitle(res);
    this.mfa_error_msg = '';
    this.is_mfaPending = true;
    this.is_logging = false;
    this.selectedIndex = 5;
    // Reset the cached child for a fresh cycle: clear the stale invalid-OTP counter,
    // then bump resetTick (restores attempts + restarts cooldown) and resendStartTick.
    this.mfa_invalidOtpTick = 0;
    this.mfa_resetTick++;
    this.mfa_resendStartTick++;
  }

  // MFA — handle (verify) emit from <app-otp-verification>.
  onMfaVerify(otp: string): void {
    if (!otp || this.is_mfaVerifying) return;
    this.mfa_error_msg = '';
    this.is_mfaVerifying = true;
    const credentials: any = { uid: this.mfa_uid, token: this.mfa_userId, otp };

    this.staffLoginService.verifyMfaOtp(credentials).pipe(takeUntil(this._onDestroy)).subscribe((res: any) => {
      if (res?.data?.[0]) { res.data = res.data[0]; }
      this.is_mfaVerifying = false;

      if (res?.code == '1' || res?.code == 1) {
        // Success — JWT issued. Same downstream flow as a normal login success.
        this.is_mfaPending = false;
        this.login_response = res;
        this.loginSessionRes();
        return;
      }

      const identifier = res?.identifier;
      if (identifier === 'INVALID_OTP') {
        this.mfa_error_msg = res?.message ?? '';
        this.mfa_invalidOtpTick++;
      } else if (identifier === 'OTP_EXPIRED') {
        this.mfa_error_msg = res?.message ?? '';
      } else if (identifier === 'OTP_NOT_FOUND') {
        this.exitMfaStep(res?.message ?? '');
      } else {
        this.mfa_error_msg = res?.message ?? '';
      }
    }, err => {
      console.log('mfa verify error', err);
      this.is_mfaVerifying = false;
      this.mfa_error_msg = err?.error?.message ?? '';
    });
  }

  // MFA — handle (resend) emit. Backend has no rate-limit; FE timer is the only gate.
  onMfaResend(): void {
    if (this.is_mfaResending) return;
    this.mfa_error_msg = '';
    this.is_mfaResending = true;
    const credentials: any = { user_id: this.mfa_userId, uid: this.mfa_uid };

    this.staffLoginService.resendMfaOtp(credentials).pipe(takeUntil(this._onDestroy)).subscribe((res: any) => {
      if (res?.data?.[0]) { res.data = res.data[0]; }
      this.is_mfaResending = false;

      if (res?.code == '1' || res?.code == 1) {
        // Always trust the latest userId from resend response.
        if (res?.data?.userId) { this.mfa_userId = res.data.userId; }
        if (res?.data?.uid) { this.mfa_uid = res.data.uid; }
        this.mfa_subtitle = this.composeMfaSubtitle(res);
        this.mfa_resendStartTick++;
      } else {
        this.mfa_error_msg = res?.message ?? '';
      }
    }, err => {
      console.log('mfa resend error', err);
      this.is_mfaResending = false;
      this.mfa_error_msg = err?.error?.message ?? '';
    });
  }

  // MFA — handle (maxAttemptsExceeded) emit. 3-strike FE counter hit zero.
  // Show inline message + 5s countdown + manual "Go to login now" link instead of instant redirect.
  onMfaMaxAttempts(): void {
    this.mfa_max_attempts_reached = true;
    this.mfa_error_msg = this.MFA_MAX_ATTEMPTS_MSG;
    this.mfa_redirect_countdown = 5;
    this.clearMfaRedirectTimer();
    this.mfaRedirectTimerId = setInterval(() => {
      this.mfa_redirect_countdown--;
      if (this.mfa_redirect_countdown <= 0) {
        this.goToLoginNow();
      }
    }, 1000);
  }

  // MFA — manual "Go to login now" link or end-of-countdown.
  goToLoginNow(): void {
    this.exitMfaStep('');
  }

  private clearMfaRedirectTimer(): void {
    if (this.mfaRedirectTimerId) {
      clearInterval(this.mfaRedirectTimerId);
      this.mfaRedirectTimerId = null;
    }
  }

  // MFA — bail back to password screen, clear FE state.
  private exitMfaStep(errorMsg: string): void {
    this.clearMfaRedirectTimer();
    this.mfa_max_attempts_reached = false;
    this.mfa_redirect_countdown = 5;
    this.is_mfaPending = false;
    this.is_mfaVerifying = false;
    this.is_mfaResending = false;
    this.mfa_userId = '';
    this.mfa_uid = '';
    this.mfa_subtitle = '';
    this.mfa_error_msg = '';
    this.error_msg = errorMsg;
    this.selectedIndex = 0;
    this.show_password_field = true;
  }

  ngOnDestroy() {
    this.clearMfaRedirectTimer();
    this._onDestroy.next();
    this._onDestroy.complete();
  }

}
